import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import type { VirtualItem } from '@tanstack/react-virtual';

import type { BaseRowData } from '@adv-frontend/data-provider';
import { useTheme } from '@direct-frontend/components';

import type { GridTable, VirtualizationOptions } from '../../../types';
import type { ScrollDirection } from '../../../hooks/useGridScrollDirection';
import { createSVGBackgroundRow, defaultColors } from '../../../lib/createSVGBackgroundRow/createSVGBackgroundRow';

import styles from '../Grid.module.css';

interface BodyBackgroundProps<RowData extends BaseRowData> {
    /**
     * Дочерний компонент.
     */
    children?: ReactNode;
    /**
     * Инстанс грида.
     */
    grid: GridTable<RowData>;
    /**
     * Настройки для react-virtual.
     */
    virtualization: VirtualizationOptions;
    /**
     * Виртуальные строки грида.
     */
    virtualRows: VirtualItem[];
    /**
     * Направление, в котором таблица была проскролена в последний раз.
     */
    verticalScrollDirection: ScrollDirection;
    /**
     * Размер ячейки
     **/
    cellSize: 's' | 'm' | 'l';
}

const getSVGBackgroundImageUrl = (svg: string) => `url('data:image/svg+xml;utf8,${svg}')`;

export function BodyBackground<RowData extends BaseRowData>({
    cellSize,
    grid,
    children,
    virtualization,
    virtualRows,
    verticalScrollDirection,
}: BodyBackgroundProps<RowData>) {
    const bodyBackground = useRef<HTMLDivElement | null>(null);
    const pinnedColumnsLeftBackground = useRef<HTMLDivElement | null>(null);

    const [colors, setColors] = useState<Record<'background' | 'skeleton' | 'border', string>>(defaultColors);
    const theme = useTheme();
    useEffect(() => {
        const node = bodyBackground.current;
        if (node) {
            const computedStyle = getComputedStyle(node);
            const background = computedStyle.getPropertyValue('--dc-color-background-1-normal');
            const skeleton = computedStyle.getPropertyValue('--dc-color-stub-stub-normal');
            const border = computedStyle.getPropertyValue('--dc-color-background-3-normal');
            setColors(prev => {
                if (prev.background !== background || prev.border !== border || prev.skeleton !== skeleton) {
                    return { background, skeleton, border };
                }
                return prev;
            });
        }
    }, [theme]);

    const gridTotalSize = grid.getTotalSize();
    const gridLeftTotalSize = grid.getLeftTotalSize();
    const gridCenterTotalSize = grid.getCenterTotalSize();

    const offsetToFirstRow = virtualRows[0]?.start ?? 0;
    const offsetToLastRow = virtualRows[virtualRows.length - 1]?.end ?? 0;

    const svgBackgroundBodyRow = useMemo(() => {
        const columns = grid.getCenterVisibleLeafColumns().map(column => ({
            width: column.getSize(),
            hasDelimiter: column.columnDef.hasDelimiter,
        }));

        return encodeURIComponent(
            createSVGBackgroundRow({
                height:
                    'strictSize' in virtualization
                        ? virtualization.strictSize(0) + 1 // +1 для бордера
                        : virtualization.estimateSize(0),
                columns,
                colors,
                cellSize,
            })
        );
        /**
         * В зависимости добавлен `gridTotalSize = grid.getTotalSize()`, чтобы пересчитывать svg бекграунд
         * при изменении размеров колонок, так как ссылка на grid статична.
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps -- DIRECT-239681
    }, [grid, gridCenterTotalSize, virtualization, colors, cellSize]);

    const svgBackgroundPinnedColumnsLeftRow = useMemo(() => {
        const columns = grid.getLeftVisibleLeafColumns().map(column => ({
            width: column.getSize(),
            hasDelimiter: column.columnDef.hasDelimiter,
        }));

        return encodeURIComponent(
            createSVGBackgroundRow({
                height:
                    'strictSize' in virtualization
                        ? virtualization.strictSize(0) + 1 // +1 для бордера
                        : virtualization.estimateSize(0),
                columns,
                colors,
                cellSize,
            })
        );
        /**
         * В зависимости добавлен `gridTotalSize = grid.getTotalSize()`, чтобы пересчитывать svg бекграунд
         * при изменении размеров колонок, так как ссылка на grid статична.
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps -- DIRECT-239681
    }, [grid, gridTotalSize, virtualization, colors, cellSize]);

    const pinnedColumnsBackgroundStyles = useMemo(() => {
        const backgroundOffsetTop = verticalScrollDirection === 'top' ? offsetToFirstRow : offsetToLastRow;

        return {
            backgroundImage: getSVGBackgroundImageUrl(svgBackgroundPinnedColumnsLeftRow),
            backgroundPositionY: `${backgroundOffsetTop}px`,
            width: `${gridLeftTotalSize}px`,
        };
    }, [
        gridLeftTotalSize,
        verticalScrollDirection,
        offsetToFirstRow,
        offsetToLastRow,
        svgBackgroundPinnedColumnsLeftRow,
    ]);

    const bodyBackgroundStyles = useMemo(() => {
        const backgroundOffsetTop = verticalScrollDirection === 'top' ? offsetToFirstRow : offsetToLastRow;

        return {
            backgroundImage: getSVGBackgroundImageUrl(svgBackgroundBodyRow),
            backgroundPositionY: `${backgroundOffsetTop}px`,
            width: `${gridCenterTotalSize}px`,
            left: `${gridLeftTotalSize}px`,
        };
    }, [
        gridCenterTotalSize,
        gridLeftTotalSize,
        verticalScrollDirection,
        offsetToFirstRow,
        offsetToLastRow,
        svgBackgroundBodyRow,
    ]);

    return (
        <>
            <div
                ref={pinnedColumnsLeftBackground}
                className={styles.pinnedColumnsLeftBackground}
                style={pinnedColumnsBackgroundStyles}
            />
            <div ref={bodyBackground} className={cn(styles.bodyBackground)} style={bodyBackgroundStyles} />
            {children}
        </>
    );
}
